/**
 * OMNITrade Landing Page
 * Marketing page promoting the crypto trading bot with focus on user control
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Shield, Key, Wallet, BarChart3, RefreshCcw, DollarSign,
  Download, ArrowRight, Check, ChevronDown, Monitor, Zap,
  Lock, Eye, Settings, TrendingUp, Users, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyncLogoWithText, SyncLogo } from '@/components/Logo';

// Brand Colors
const colors = {
  deepNavy: '#1E3A5F',
  skyBlue: '#5A9BD5',
  lightGray: '#F2F7F2',
};

const features = [
  {
    icon: Key,
    title: "Your Keys, Your Control",
    description: "Generate API keys with exactly the permissions you choose. Read-only, trade-only, or full access - you decide what the bot can do."
  },
  {
    icon: Wallet,
    title: "Your Exchange Account",
    description: "Trade directly from your own exchange account. Your funds never leave your control - we simply execute your strategy."
  },
  {
    icon: Lock,
    title: "Locally Encrypted Keys",
    description: "Your API keys are encrypted with AES-256 and stored only on your computer. They never touch our servers."
  },
  {
    icon: RefreshCcw,
    title: "Override Any Transaction",
    description: "Disagree with the bot's decision? Override any trade instantly. You always have the final say on your positions."
  },
  {
    icon: Shield,
    title: "Risk Management Built-In",
    description: "Set stop-losses, daily limits, and position sizes. The bot respects your boundaries and protects your capital."
  },
  {
    icon: DollarSign,
    title: "Pay Only for What You Use",
    description: "No monthly subscriptions. No lock-ins. Simply pay a small credit fee for each transaction the bot executes."
  }
];

const benefits = [
  "No monthly fees or subscriptions",
  "Free to sign up and download",
  "Paper trading mode included",
  "Multiple exchange support",
  "24/7 automated trading",
  "Real-time performance tracking"
];

const stats = [
  { value: "10+", label: "Supported Exchanges" },
  { value: "6", label: "Trading Strategies" },
  { value: "24/7", label: "Automated Trading" },
  { value: "100%", label: "Your Control" }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <SyncLogoWithText variant="dark" />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-300 hover:text-white transition-colors text-sm">Features</a>
              <a href="#how-it-works" className="text-gray-300 hover:text-white transition-colors text-sm">How It Works</a>
              <a href="/interface-guide" className="text-gray-300 hover:text-white transition-colors text-sm">Interface Guide</a>
              <a href="/strategies" className="text-gray-300 hover:text-white transition-colors text-sm">Strategies</a>
              <a href="#pricing" className="text-gray-300 hover:text-white transition-colors text-sm">Pricing</a>
            </div>
            
            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/auth')}
                data-testid="nav-login-btn"
              >
                Log In
              </Button>
              <Button 
                className="bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                onClick={() => navigate('/auth?mode=register')}
                data-testid="nav-signup-btn"
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#5A9BD5]/10 border border-[#5A9BD5]/20 rounded-full text-[#5A9BD5] text-sm mb-8">
            <Zap className="w-4 h-4" />
            Automated Crypto Trading with Complete Control
          </div>
          
          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Trade Smarter, Not Harder.
            <br />
            <span className="text-[#5A9BD5]">You're Always in Control.</span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto mb-10">
            OMNITrade is an intelligent crypto trading bot that runs on your computer, uses your exchange account, 
            and keeps your API keys encrypted locally. No hidden fees. No loss of control. Just smarter trading.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button 
              size="lg"
              className="bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white px-8 py-6 text-lg rounded-xl"
              onClick={() => navigate('/auth?mode=register')}
              data-testid="hero-signup-btn"
            >
              Sign Up Free & Download
              <Download className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-white/5 px-8 py-6 text-lg rounded-xl"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
            >
              See How It Works
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            {benefits.slice(0, 4).map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#5A9BD5] mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Interface Preview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Powerful Trading Interface
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-6">
              A comprehensive dashboard with real-time data, strategy indicators, and complete control over your automated trading.
            </p>
            <div className="flex items-center justify-center gap-4 mb-8">
              <Button 
                variant="outline"
                className="border-[#5A9BD5]/50 text-[#5A9BD5] hover:bg-[#5A9BD5]/10"
                onClick={() => navigate('/interface-guide')}
              >
                <Monitor className="w-4 h-4 mr-2" />
                Interface Guide
              </Button>
              <Button 
                variant="outline"
                className="border-[#5A9BD5]/50 text-[#5A9BD5] hover:bg-[#5A9BD5]/10"
                onClick={() => navigate('/strategies')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Trading Strategies
              </Button>
            </div>
          </div>
          
          {/* Screenshot with glow effect */}
          <div className="relative">
            <div className="absolute inset-0 bg-[#5A9BD5]/20 blur-3xl rounded-full"></div>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-slate-900/50">
              <img 
                src="/assets/app-interface.png" 
                alt="OMNITrade Trading Interface" 
                className="w-full h-auto"
              />
            </div>
          </div>
          
          {/* Feature highlights below screenshot */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6 bg-white/[0.03] rounded-xl border border-white/5">
              <TrendingUp className="w-8 h-8 text-[#5A9BD5] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Real-Time Charts</h3>
              <p className="text-gray-400 text-sm">Candlestick and line charts with multiple timeframes</p>
            </div>
            <div className="text-center p-6 bg-white/[0.03] rounded-xl border border-white/5">
              <BarChart3 className="w-8 h-8 text-[#5A9BD5] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Strategy Indicators</h3>
              <p className="text-gray-400 text-sm">RSI, EMA, ADX, Bollinger Bands and more</p>
            </div>
            <div className="text-center p-6 bg-white/[0.03] rounded-xl border border-white/5">
              <DollarSign className="w-8 h-8 text-[#5A9BD5] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Performance Tracking</h3>
              <p className="text-gray-400 text-sm">Detailed P&L with win rate and trade history</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Complete Control Over Your Trading
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Unlike other bots that require access to your funds, OMNITrade keeps you in the driver's seat at all times.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:bg-white/[0.05] hover:border-[#5A9BD5]/30 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#5A9BD5]/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#5A9BD5]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From signup to your first automated trade in just a few simple steps.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Create Account", desc: "Sign up for free with just your email. No credit card required.", icon: Users },
              { step: "2", title: "Download App", desc: "Get the desktop app for Windows, macOS, or Linux.", icon: Download },
              { step: "3", title: "Connect Exchange", desc: "Add your API keys. They're encrypted and stored locally only.", icon: Key },
              { step: "4", title: "Start Trading", desc: "Choose a strategy, set your limits, and let OMNITrade work for you.", icon: TrendingUp }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                {idx < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#5A9BD5]/50 to-transparent" />
                )}
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#5A9BD5] rounded-2xl flex items-center justify-center mx-auto mb-4 relative z-10">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-[#5A9BD5] text-sm font-medium mb-2">Step {item.step}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              No monthly subscriptions. No hidden fees. Pay only for the trades you execute.
            </p>
          </div>
          
          <div className="max-w-lg mx-auto">
            <div className="bg-gradient-to-b from-[#5A9BD5]/10 to-transparent border border-[#5A9BD5]/30 rounded-3xl p-8 text-center">
              <div className="text-[#5A9BD5] text-sm font-medium mb-2">PAY AS YOU TRADE</div>
              <div className="text-5xl font-bold text-white mb-2">Credits</div>
              <p className="text-gray-400 mb-8">Purchase credits and use them for each trade</p>
              
              <ul className="space-y-4 mb-8 text-left">
                {[
                  "Free signup with bonus credits",
                  "Small fee per executed trade",
                  "Tiered pricing for larger trades",
                  "No monthly commitments",
                  "Credits never expire",
                  "Referral bonuses available"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                size="lg"
                className="w-full bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white py-6 text-lg rounded-xl"
                onClick={() => navigate('/auth?mode=register')}
                data-testid="pricing-signup-btn"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#5A9BD5]/20 via-[#5A9BD5]/10 to-[#5A9BD5]/20">
        <div className="max-w-4xl mx-auto text-center">
          <SyncLogo className="w-16 h-16 mx-auto mb-6" variant="dark" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Take Control of Your Trading?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Join thousands of traders who trust OMNITrade to execute their strategies while keeping them in complete control.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              className="bg-white text-[#1E3A5F] hover:bg-gray-100 px-8 py-6 text-lg rounded-xl font-semibold"
              onClick={() => navigate('/auth?mode=register')}
              data-testid="cta-signup-btn"
            >
              Sign Up Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl"
              onClick={() => navigate('/auth')}
            >
              Already have an account? Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <SyncLogoWithText variant="dark" />
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-gray-300 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-gray-300 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-gray-300 transition-colors">Support</a>
            </div>
            <div className="text-sm text-gray-500">
              © {new Date().getFullYear()} OMNITrade. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
